import { useEffect, useRef, useState } from "react";

import { Check, CheckCheck, RotateCcw, X } from "lucide-react";

import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import type { ReviewStateFields, ReviewStatus } from "../api";

/**
 * The review desk's state control: New → Accepted → Done, or Rejected, plus
 * a note. One control for article reviews, impact rows and gap outlines, so
 * the desk and the scan read the same way.
 *
 * Not `ReviewVerdict`: that panel is the reconciliation close's three-way
 * approve/flag/clear verdict. This is a four-state work queue — "I will act
 * on this", "I have acted on it", "not doing this" — and the words matter to
 * the person working it, so it keeps its own vocabulary rather than aliasing
 * the close's. Same draft-state discipline though: re-seeded only when the
 * item changes, never wiped by a background refetch mid-note.
 */
export function ReviewStateControl({
  itemKey,
  state,
  canAct,
  saving,
  onSave,
  compact = false,
  className,
}: {
  itemKey: string;
  state: ReviewStateFields;
  canAct: boolean;
  saving?: boolean;
  onSave: (next: { status: ReviewStatus; note: string }) => void;
  /** Tighter layout for a table row / card footer. */
  compact?: boolean;
  className?: string;
}) {
  const [draft, setDraft] = useState<ReviewStatus>(state.status);
  const [note, setNote] = useState(state.note ?? "");
  const seeded = useRef<string | null>(null);

  useEffect(() => {
    if (seeded.current === itemKey) return;
    seeded.current = itemKey;
    setDraft(state.status);
    setNote(state.note ?? "");
  }, [itemKey, state.status, state.note]);

  // After a save lands the server state catches up with the draft; keep the
  // badge honest without re-seeding a note that may be mid-edit.
  useEffect(() => {
    setDraft(state.status);
  }, [state.status]);

  const dirty = draft !== state.status || note !== (state.note ?? "");

  return (
    <div
      className={cn(
        "space-y-2",
        !compact && "rounded-md border px-4 py-3",
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          <StateButton
            active={draft === "accepted"}
            activeClass="border-warning bg-warning-light text-warning"
            disabled={!canAct}
            onClick={() => setDraft("accepted")}
            title="I agree and will update the article"
          >
            <Check className="h-3.5 w-3.5" /> Accept
          </StateButton>
          <StateButton
            active={draft === "done"}
            activeClass="border-success bg-success-light text-success"
            disabled={!canAct}
            onClick={() => setDraft("done")}
            title="The article has been updated"
          >
            <CheckCheck className="h-3.5 w-3.5" /> Done
          </StateButton>
          <StateButton
            active={draft === "rejected"}
            activeClass="border-border bg-muted text-foreground"
            disabled={!canAct}
            onClick={() => setDraft("rejected")}
            title="Not doing this — the reviewer is wrong or it is not worth it"
          >
            <X className="h-3.5 w-3.5" /> Reject
          </StateButton>
          {draft !== "new" ? (
            <StateButton
              active={false}
              activeClass=""
              disabled={!canAct}
              onClick={() => setDraft("new")}
              title="Back to new"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Reset
            </StateButton>
          ) : null}
        </div>
        <StatusBadge value={`review_${state.status}`} />
      </div>

      <Textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        disabled={!canAct}
        rows={compact ? 2 : 3}
        aria-label="Review note"
        placeholder={
          draft === "rejected"
            ? "Why not? (so the reviewer can learn from it)"
            : "Optional note — what you changed, or what to remember"
        }
        className="text-sm"
      />

      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="sm"
          onClick={() => onSave({ status: draft, note })}
          disabled={!canAct || !dirty || !!saving}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
        {state.reviewed_at ? (
          <span className="text-xs text-muted-foreground">
            Last set by {state.reviewed_by ?? "—"} on{" "}
            {new Date(state.reviewed_at).toLocaleString()}
          </span>
        ) : null}
        {!canAct ? (
          <span className="text-xs text-muted-foreground">
            Read-only — working the desk needs the Knowledge Base write
            permission.
          </span>
        ) : null}
      </div>
    </div>
  );
}

function StateButton({
  active,
  activeClass,
  onClick,
  disabled,
  title,
  children,
}: {
  active: boolean;
  activeClass: string;
  onClick: () => void;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        active ? activeClass : "border-border hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}
